// Shell-wide verdict chooser state (U0): `!` on a card, thread, cleanup or Set aside row opens it
// through here, and the one VerdictHost in the shell shows it over any screen.
import { useSyncExternalStore } from "react";
import { toast } from "sonner";
import { useCommand } from "@web/shell/commands";
import type { VerdictTarget } from "./verdicts";

export interface VerdictState {
  open: boolean;
  target: VerdictTarget | null;
  /** Bumps on every open so the chooser starts fresh. */
  seq: number;
}

let state: VerdictState = { open: false, target: null, seq: 0 };
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export const verdictStore = {
  open(target: VerdictTarget) {
    state = { open: true, target, seq: state.seq + 1 };
    emit();
  },
  close() {
    if (!state.open) return;
    state = { ...state, open: false };
    emit();
  },
  get(): VerdictState {
    return state;
  },
};

export function useVerdictState(): VerdictState {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => state,
    () => state,
  );
}

/**
 * Binds `!` (and the palette's "Tell MeeMail what it got wrong…") to whatever this screen has in
 * view. `target` is read when the key is pressed, so it always matches what is on screen.
 */
export function useVerdictCommand(target: () => VerdictTarget | null, enabled = true): void {
  useCommand(
    "verdict.open",
    () => {
      const t = target();
      if (t) verdictStore.open(t);
      else toast("Pick a card or a thread first, then press !");
    },
    enabled,
  );
}
